import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { CheckCircle2, Lightbulb, Timer, Trophy, XCircle } from 'lucide-react';
import { db } from '../../../firebase/config';
import { cn } from '../../../lib/utils';
import { Card, CardContent } from '../../ui/card';

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
};

const DailyLeaderboard = ({ challengeId, currentUserId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!challengeId) return undefined;
    const q = query(
      collection(db, 'dailyChallengeAttempts'),
      where('challengeId', '==', challengeId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        items.sort((a, b) => {
          const ap = a.points || 0;
          const bp = b.points || 0;
          if (bp !== ap) return bp - ap;
          const at = a.timeSeconds ?? Infinity;
          const bt = b.timeSeconds ?? Infinity;
          return at - bt;
        });
        setRows(items);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [challengeId]);

  return (
    <Card className="border-border/60 bg-background/40">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            <Trophy className="h-3 w-3 text-heat" />
            Today's leaderboard
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            {rows.length} {rows.length === 1 ? 'player' : 'players'}
          </span>
        </div>

        {loading ? (
          <div className="space-y-1.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded-sm bg-muted/50" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">No entries yet today.</p>
        ) : (
          <table className="w-full text-sm tabular-nums">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <th className="w-8 py-1.5 text-left">#</th>
                <th className="py-1.5 text-left">Player</th>
                <th className="py-1.5 text-right">Points</th>
                <th className="hidden py-1.5 text-center sm:table-cell">Guess</th>
                <th className="py-1.5 text-center">
                  <span className="inline-flex items-center gap-1">
                    <span className="hidden sm:inline">Tries</span>
                    <span className="sm:hidden">T</span>
                  </span>
                </th>
                <th className="py-1.5 text-center">
                  <Lightbulb className="mx-auto h-3 w-3" />
                </th>
                <th className="hidden py-1.5 text-right sm:table-cell">
                  <Timer className="ml-auto h-3 w-3" />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isMe = currentUserId && r.userId === currentUserId;
                const rank = i + 1;
                return (
                  <tr key={r.id} className={cn('border-t border-border/40', isMe && 'bg-heat/10')}>
                    <td className="py-2 font-mono text-xs text-muted-foreground">
                      {rank === 1 ? (
                        <span className="text-gold">1</span>
                      ) : rank === 2 ? (
                        <span className="text-silver">2</span>
                      ) : rank === 3 ? (
                        <span className="text-bronze">3</span>
                      ) : (
                        rank
                      )}
                    </td>
                    <td className="py-2 font-medium">
                      <span className={cn('truncate', isMe && 'text-heat')}>
                        {r.username || 'Anonymous'}
                        {isMe && (
                          <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-heat">
                            you
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2 text-right font-semibold">{r.points ?? 0}</td>
                    <td className="hidden py-2 text-center sm:table-cell">
                      {r.isCorrect ? (
                        <CheckCircle2 className="mx-auto h-4 w-4 text-[hsl(var(--winner))]" />
                      ) : (
                        <XCircle className="mx-auto h-4 w-4 text-destructive" />
                      )}
                    </td>
                    <td className="py-2 text-center text-muted-foreground">{r.attempts ?? '—'}</td>
                    <td className="py-2 text-center text-muted-foreground">{r.hintsUsed ?? 0}</td>
                    <td className="hidden py-2 text-right text-muted-foreground sm:table-cell">
                      {formatTime(r.timeSeconds)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyLeaderboard;
